import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Flag, MessageCircle } from "lucide-react";
import { DiaryPoster, showAccent, HeartButton } from "@/components/DiaryPoster";
import { useDialog } from "@/components/Dialogs";

export function errText(e, fallback) {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

const LIST_REACTIONS = [["heart", "❤️", "Love this"], ["seen_too", "👀", "I've seen these too"], ["want_to_see", "🎟️", "On my list"]];
const NOTE_REACTIONS = [["heart", "❤️", "Love this"]];

/** Reaction buttons for a list or a note. kind is "list" or "note". Pass canReact={false} for your own items (counts only). */
export function ContentReactions({ kind, item, canReact = true, onChange }) {
  const [busy, setBusy] = useState(false);
  const set = kind === "list" ? LIST_REACTIONS : NOTE_REACTIONS;
  const counts = item.reactions || {};
  const mine = item.mine || [];
  const toggle = async (type) => {
    if (!canReact || busy) return;
    const on = !mine.includes(type);
    setBusy(true);
    try {
      await api.put(`/content/${kind}/${item.id}/reaction`, { type, on });
      onChange({ ...item, mine: on ? [...mine, type] : mine.filter((t) => t !== type),
        reactions: { ...counts, [type]: Math.max(0, (counts[type] || 0) + (on ? 1 : -1)) } });
    } catch { toast.error("Could not save that reaction. Please try again."); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 flex flex-wrap gap-2" data-testid={`${kind}-reactions`}>
      {set.map(([type, emoji, label]) => {
        const n = counts[type] || 0;
        const on = mine.includes(type);
        if (!canReact && n === 0) return null;
        if (type === "heart") return <HeartButton key={type} on={on} n={n} canReact={canReact} busy={busy} onClick={() => toggle(type)} testid={`${kind}-react-heart`} label={label} />;
        return (
          <button key={type} type="button" disabled={!canReact || busy} onClick={() => toggle(type)} aria-pressed={on}
            data-testid={`${kind}-react-${type}`} title={label}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${on ? "border-[#C89B3C] bg-[#C89B3C]/20 text-foreground" : "border-border text-muted-foreground"} ${canReact ? "hover:border-[#C89B3C]/60" : "cursor-default"}`}>
            <span aria-hidden="true">{emoji}</span> {label}{n > 0 && <span className="font-semibold text-[hsl(var(--gold))]">{n}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** Owner only: who reacted, with a Remove link for each. */
export function ReactedBy({ item, onRemoved }) {
  if (!item.reacted_by?.length) return null;
  const remove = async (r) => {
    try { await api.delete(`/content-reactions/${r.id}`); onRemoved(r.id); toast.success("Reaction removed"); }
    catch { toast.error("Could not remove that. Please try again."); }
  };
  return (
    <ul className="mt-2 space-y-1 text-xs text-muted-foreground" data-testid="content-reacted-by">
      {item.reacted_by.map((r) => (
        <li key={r.id}>{r.username}: {r.type.replace("_", " ")} <button type="button" className="ml-2 underline hover:text-red-400" onClick={() => remove(r)}>Remove</button></li>
      ))}
    </ul>
  );
}

/** Anyone who can see a list or note, other than its owner, can report it. */
export function ReportLink({ kind, id }) {
  const [sent, setSent] = useState(false);
  const dialog = useDialog();
  if (sent) return <span className="text-xs text-muted-foreground" data-testid="reported">Thanks, we'll take a look.</span>;
  const go = async () => {
    const reason = await dialog.prompt({ title: "Report this", body: "Tell us what is wrong. We'll take a look, and the person won't be told it was you.",
      label: "What is wrong with this? (optional)", maxLength: 300, confirmLabel: "Send report" });
    if (reason === null) return;
    try { await api.post(`/content/${kind}/${id}/report`, { reason }); setSent(true); }
    catch { toast.error("Could not send that report. Please try again."); }
  };
  return (
    <>
      <button type="button" onClick={go} data-testid={`report-${kind}`} className="inline-flex items-center gap-1 text-xs text-muted-foreground underline hover:text-foreground">
        <Flag className="h-3 w-3" /> Report
      </button>
      {dialog.node}
    </>
  );
}

export const VIS_LABEL = { private: "Only me", friends: "My friends", public: "Anyone with the link" };

/** A numbered (or bulleted) list of shows, each with its one-line reason. */
export function ListBody({ list }) {
  return (
    <ol className="mt-4 space-y-3" data-testid="list-items">
      {list.items.map((it, i) => (
        <li key={it.show_id} className="flex gap-3 overflow-hidden rounded-xl border border-border bg-background/40" style={{ borderLeft: `4px solid ${showAccent(it.name)}` }} data-testid="list-item">
          {list.ranked && <span className="flex w-9 flex-shrink-0 items-center justify-center font-serif text-2xl font-bold text-[#C89B3C]" aria-label={`Number ${i + 1}`}>{i + 1}</span>}
          <div className="h-20 w-14 flex-shrink-0 self-stretch bg-secondary"><DiaryPoster src={it.image_url} name={it.name} /></div>
          <div className="min-w-0 flex-1 py-2.5 pr-3">
            {it.is_custom || !it.slug
              ? <span className="font-serif text-lg font-semibold leading-tight">{it.name}</span>
              : <Link to={`/shows/${it.slug}`} className="font-serif text-lg font-semibold leading-tight hover:text-primary">{it.name}</Link>}
            {it.venue && <p className="text-xs text-muted-foreground">{it.venue}</p>}
            {it.reason && <p className="mt-1 text-sm italic text-foreground/80">“{it.reason}”</p>}
          </div>
        </li>
      ))}
    </ol>
  );
}

/** A note's text. Plain text only: line breaks are kept, nothing is turned into a link. */
export function NoteBody({ note }) {
  const [open, setOpen] = useState(false);
  const long = (note.body || "").length > 600;
  const text = long && !open ? `${note.body.slice(0, 600).trimEnd()}…` : note.body;
  return (
    <div className="mt-2">
      {note.show_name && (
        <p className="text-xs text-muted-foreground">About{" "}
          {note.show_slug ? <Link to={`/shows/${note.show_slug}`} className="text-[#C89B3C] underline">{note.show_name}</Link> : note.show_name}</p>
      )}
      <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground/90" data-testid="note-text">{text}</p>
      {long && <button type="button" className="mt-1 text-sm text-[#C89B3C] underline" onClick={() => setOpen(!open)}>{open ? "Show less" : "Read more"}</button>}
    </div>
  );
}

export function when(iso) {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; }
}


const CLIMIT = 500;

function Comment({ c, canPost, isReply, onReply, onChanged }) {
  const dialog = useDialog();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(c.text);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/comments/${c.id}`, { text }); setEditing(false); onChanged(); }
    catch (err) { toast.error(errText(err, "Could not save that. Please try again.")); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!(await dialog.confirm({ title: isReply ? "Delete this reply?" : "Delete this comment?", body: isReply ? "" : "Any replies to it will be deleted too.", confirmLabel: "Delete", danger: true }))) return;
    try { await api.delete(`/comments/${c.id}`); onChanged(); }
    catch { toast.error("Could not delete that. Please try again."); }
  };
  return (
    <div className={isReply ? "ml-6 border-l-2 border-[#C89B3C]/50 pl-3" : ""} data-testid={isReply ? "reply" : "comment"}>
      {dialog.node}
      <div className="flex gap-3 rounded-xl border border-border bg-background/70 p-3.5 sm:p-4">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#C89B3C] font-serif text-base font-bold text-black" aria-hidden="true">{(c.username || "?")[0].toUpperCase()}</span>
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-muted-foreground">
            <Link to={`/u/${c.username}`} className="text-base font-semibold text-foreground hover:underline">{c.username}</Link>
            {c.by_owner && <span className="rounded bg-[#C89B3C]/20 px-1.5 py-0.5 text-xs text-[hsl(var(--gold))]" data-testid="author-tag">Author</span>}
            <span>{when(c.created_at)}{c.edited && " · edited"}</span>
            {c.hidden && <span className="rounded bg-red-500/20 px-1.5 py-0.5 text-xs text-red-300" data-testid="hidden-flag">Hidden by us</span>}
          </p>
          {editing ? (
            <div className="mt-2">
              <textarea aria-label="Edit comment" data-testid="comment-edit-box" rows={3} maxLength={CLIMIT} value={text} onChange={(e) => setText(e.target.value)} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-base" />
              <div className="mt-2 flex gap-4 text-sm">
                <button type="button" disabled={busy} onClick={save} data-testid="comment-edit-save" className="font-semibold text-[#C89B3C] underline">Save</button>
                <button type="button" onClick={() => { setEditing(false); setText(c.text); }} className="underline">Cancel</button>
              </div>
            </div>
          ) : <p className="mt-1 whitespace-pre-wrap break-words text-base leading-relaxed text-foreground" data-testid="comment-text">{c.text}</p>}
          {!editing && (
            <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-muted-foreground">
              {canPost && !isReply && <button type="button" onClick={() => onReply(c)} data-testid="comment-reply" className="py-1 font-medium text-[#C89B3C] hover:underline">Reply</button>}
              {c.mine && !c.hidden && <button type="button" onClick={() => setEditing(true)} data-testid="comment-edit" className="py-1 hover:text-foreground hover:underline">Edit</button>}
              {c.can_delete && <button type="button" onClick={remove} data-testid="comment-delete" className="py-1 hover:text-red-400 hover:underline">Delete</button>}
              {c.can_report && <ReportLink kind="comment" id={c.id} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Comments on a list or note, for the author and the author's friends only.
 *  count: how many there are (shown on the button); onCount: called with the new total after a change. */
export function Comments({ kind, id, count = 0, onCount }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [busy, setBusy] = useState(false);
  const onCountRef = useRef(onCount);   // always the latest callback, so `load` can stay stable
  onCountRef.current = onCount;

  const load = useCallback(() => api.get(`/content/${kind}/${id}/comments`).then((r) => {
    setData(r.data);
    const total = r.data.comments.reduce((n, c) => n + 1 + c.replies.filter((x) => !x.hidden).length, 0) - r.data.comments.filter((c) => c.hidden).length;
    if (onCountRef.current) onCountRef.current(Math.max(0, total));
  }).catch(() => setData({ comments: [], off: true, can_comment: false })), [kind, id]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const post = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/content/${kind}/${id}/comments`, { text, parent_id: replyTo?.id || null });
      setText(""); setReplyTo(null); await load();
    } catch (err) { toast.error(errText(err, "Could not post that. Please try again.")); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-5 border-t border-border pt-4" data-testid={`${kind}-comments`}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} data-testid="comments-toggle"
        className="flex w-full items-center justify-between gap-3 rounded-xl border border-[#C89B3C]/50 bg-[#C89B3C]/10 px-4 py-3 text-left text-base font-semibold text-[hsl(var(--gold))] hover:bg-[#C89B3C]/20">
        <span className="inline-flex items-center gap-2"><MessageCircle className="h-5 w-5" />{open ? "Hide comments" : "Comments"}</span>
        {count > 0 && <span className="rounded-full bg-[#C89B3C] px-2.5 py-0.5 text-sm font-bold text-black" data-testid="comments-count">{count}</span>}
      </button>
      {open && (!data ? <p className="mt-2 text-sm text-muted-foreground">Loading…</p> : (
        <div className="mt-4 space-y-4">
          {data.comments.length === 0 && <p className="text-base text-muted-foreground" data-testid="no-comments">No comments yet. Be the first to say something.</p>}
          {data.comments.map((c) => (
            <div key={c.id} className="space-y-2.5">
              <Comment c={c} canPost={data.can_comment} onReply={setReplyTo} onChanged={load} />
              {c.replies.map((r) => <Comment key={r.id} c={r} isReply onChanged={load} />)}
            </div>
          ))}
          {data.off && <p className="text-xs text-muted-foreground" data-testid="comments-off">Comments are turned off here.</p>}
          {data.can_comment && (
            <form onSubmit={post}>
              {replyTo && <p className="mb-2 text-sm text-muted-foreground">Replying to {replyTo.username} <button type="button" className="ml-1 underline" onClick={() => setReplyTo(null)}>Cancel</button></p>}
              <textarea aria-label="Write a comment" data-testid="comment-box" rows={3} maxLength={CLIMIT} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a comment (plain text)" className="w-full rounded-xl border border-[#C89B3C]/40 bg-background px-4 py-3 text-base leading-relaxed focus:border-[#C89B3C] focus:outline-none" />
              <div className="mt-1 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{text.length} / {CLIMIT}</span>
                <button type="submit" disabled={busy || !text.trim()} data-testid="comment-post" className="rounded-full bg-[#C89B3C] px-7 py-2.5 text-base font-semibold text-black disabled:opacity-50">{busy ? "Posting…" : "Post"}</button>
              </div>
            </form>
          )}
        </div>
      ))}
    </div>
  );
}